import { useRouteLoaderData } from "@remix-run/react";
import { captureMessage } from "@sentry/remix";
import { AlertCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ServiceTypeSelect from "~/components/booking/flow/ServiceTypeSelect";
import { SHOW_CONFIRM_ON_AUTO_ADVANCE_STEPS } from "~/components/booking/flow/confirmButton";
import { Button } from "~/components/ui/Button";
import { useBookingCart as useBookingCartStore } from "~/stores/bookingCartContext";

const ServiceTypeStep = ({
  disableConfirm = false,
  onConfirm,
  onChange,
  onCancel,
  onResetToStep0,
  setServices,
  services,
  selectedDate,
  guestsCount,
  selectedAvailability,
  isVoucherBooking = false,
  defaultSelection,
  onSelectionChange,
}: {
  disableConfirm?: boolean;
  onChange: () => void;
  onCancel: () => void;
  onConfirm: (selectedService: any, apiService?: any, packages?: any[]) => void;
  onResetToStep0?: () => void;
  setServices: (services: any[]) => void;
  services: any[];
  selectedDate: Date;
  guestsCount: { male: number; female: number };
  selectedAvailability: any;
  isVoucherBooking?: boolean;
  // Selezione tenuta dal genitore: permette di re-inizializzare la selezione
  // al (ri)mount, così un eventuale rimonta del componente non la perde.
  defaultSelection?: { cmsService: any; apiService: any } | null;
  onSelectionChange?: (cmsService: any, apiService: any) => void;
}) => {
  const { t } = useTranslation();
  const rootData = useRouteLoaderData<any>("root");
  const { bookingCart } = useBookingCartStore();
  const [hasErrors, setHasErrors] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCmsService, setSelectedCmsService] = useState<any>(
    () => defaultSelection?.cmsService,
  );
  const [selectedApiService, setSelectedApiService] = useState<any>(
    () => defaultSelection?.apiService,
  );
  const [isSkipped, setIsSkipped] = useState<any>(false);

  useEffect(() => {
    if (
      selectedDate &&
      !isNaN(selectedDate.getTime()) &&
      selectedAvailability
    ) {
      setIsSkipped(false);
      fetchServices(); // this is necessarry for packages
    }
  }, [selectedAvailability]);

  const fetchServices = async () => {
    if (!selectedDate) {
      setHasErrors(true);
      setErrorMessage(t("Seleziona una data"));
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setHasErrors(false);
    setErrorMessage(null);
    try {
      const formattedDate = selectedDate.toISOString().split("T")[0];
      const vouchers = isVoucherBooking ? bookingCart?.vouchers || [] : [];

      let servicesFilter: string[] = [];

      if (isVoucherBooking) {
        const nonUpsellingVouchers = vouchers.filter(
          (v) => v.voucherData?.OnlyIntegration !== true,
        );
        servicesFilter = [
          ...new Set(
            nonUpsellingVouchers
              .map((v) => v.voucherData?.ServicesIncludedCodes || [])
              .flat(),
          ),
        ];
      }

      let urlWithParams = `/api/booking/availability/hours?checkinDate=${formattedDate}&men=${
        guestsCount.male
      }&women=${guestsCount.female}${
        servicesFilter.length > 0 ? `&services=${servicesFilter.join(",")}` : ""
      }`;

      if (isVoucherBooking && vouchers.length > 0) {
        const vouchersPayload = vouchers.map((v) => ({
          VoucherCode: v.code,
          GiftCardType: v.giftCardType || "",
        }));
        urlWithParams += `&vouchers=${encodeURIComponent(
          JSON.stringify(vouchersPayload),
        )}`;
      }

      const response = await fetch(urlWithParams);
      const data: any = await response.json();

      if (data && !data.error) {
        const hasAnyNonPackage = !!data.find((s: any) => s.Type !== "Package");
        // Filtriamo i Ticket solo se l'utente ha un voucher di categoria
        // PACKAGE (vedi ServiceTypeSelect per la motivazione).
        const hasPackageVoucher = !!bookingCart?.vouchers?.some(
          (v: any) => v.voucherData?.Category === "PACKAGE",
        );

        const upsellingCategoryIds = new Set<number>(
          (rootData?.upsellingServiceCategories || [])
            .map((c: any) => c.id)
            .filter((id: any) => typeof id === "number"),
        );

        const servicesToShow: any[] = [];
        rootData.servicesData?.forEach((cmsService: any) => {
          // Se il cmsService è categorizzato come upselling nella taxonomy
          // WP (`service_category`), i suoi ghost show_anyway vanno mostrati
          // nello step upselling, non qui. Skip.
          const taxIds: number[] = Array.isArray(cmsService?.service_category)
            ? cmsService.service_category
            : [];
          const isUpsellingCms =
            taxIds.length > 0 &&
            taxIds.every((id) => upsellingCategoryIds.has(id));
          if (isUpsellingCms) return;
          const codes = cmsService.acf?.codes || [];
          codes.forEach((codeObj: any) => {
            const code = codeObj?.code;
            if (!code) return;

            const apiService = data?.find((s: any) => s.Code === code);
            // Code con `show_anyway` in ACF: card sempre visibile, anche se
            // l'API non ritorna nulla. La card non sarà selezionabile e
            // mostrerà `show_anyway_text` al posto di prezzo/availability.
            // ACF REST può restituire boolean / "1" / "0" / 1 / 0 → check robusto.
            const rawFlag = codeObj?.show_anyway;
            const showAnywayFlag =
              rawFlag === true ||
              rawFlag === 1 ||
              rawFlag === "1" ||
              rawFlag === "true";
            if (!apiService) {
              // Le card "show_anyway" (info-only, sempre visibili) NON vanno
              // mostrate nel flusso VOUCHER: lì si possono selezionare solo i
              // servizi effettivamente coperti dal voucher.
              if (showAnywayFlag && !isVoucherBooking) {
                servicesToShow.push({
                  cmsService,
                  apiService: {
                    Code: code,
                    Type: "ShowAnyway",
                    Name:
                      codeObj.short_description ||
                      cmsService.acf?.title ||
                      code,
                    isShowAnyway: true,
                    showAnywayText: codeObj.show_anyway_text || "",
                    Availability: [],
                  },
                });
              }
              return;
            }

            const isPackage = apiService.Type === "Package";

            // Voucher mode: se il voucher è di tipo Package, mostra solo
            // pacchetti. Altrimenti (Ticket / upselling) lascia visibili tutti.
            if (isVoucherBooking && hasPackageVoucher && !isPackage) {
              return;
            }

            // Modalità normale: i pacchetti NON si mostrano tra le card dei
            // servizi quando ci sono anche servizi normali. Restano in `data`
            // (quindi compaiono come code del loro servizio nello step durata),
            // qui li escludiamo solo dalla selezione servizio.
            if (!isVoucherBooking && hasAnyNonPackage && isPackage) {
              return;
            }

            // Dedupe per cmsService.id solo per i non-package (i package sono mostrati tutti)
            if (
              !isPackage &&
              servicesToShow.find(
                (s: any) =>
                  s.cmsService.id === cmsService.id &&
                  s.apiService.Type !== "Package",
              )
            ) {
              return;
            }

            servicesToShow.push({ cmsService, apiService });
          });
        });

        // Le card show_anyway sono ghost (l'API non ritorna nulla) e non
        // partecipano alle euristiche di auto-skip / "tutti pacchetti".
        const realServicesToShow = servicesToShow.filter(
          (s: any) => !s.apiService?.isShowAnyway,
        );

        const visibleAreAllPackages =
          realServicesToShow.length > 0 &&
          realServicesToShow.every(
            (s: any) => s.apiService.Type === "Package",
          );

        if (
          visibleAreAllPackages &&
          realServicesToShow.filter(
            (s: any) => s.apiService.Type === "Package",
          ).length > 1
        ) {
          const packages = realServicesToShow.filter(
            (s: any) => s.apiService.Type === "Package",
          );

          if (packages.length > 1) {
            const firstStepServices = packages
              .map((pkg: any) => {
                const firstStep = pkg.apiService.PackageComposition?.find(
                  (step: any) => step.StepNr === 1,
                );
                return firstStep?.ServicesList?.[0]?.Code;
              })
              .filter(Boolean);

            if (firstStepServices.length === packages.length) {
              const allSameService = firstStepServices.every(
                (code: string) => code === firstStepServices[0],
              );

              if (allSameService && onConfirm) {
                onConfirm(null, null, packages);
                return;
              }
            }
          }
        }

        // Auto-confirm solo se c'è esattamente un servizio REALE (non un
        // ghost show_anyway) — altrimenti l'utente non potrebbe più
        // selezionare nulla.
        if (
          realServicesToShow.length === 1 &&
          servicesToShow.length === realServicesToShow.length
        ) {
          onConfirm(
            realServicesToShow[0].cmsService,
            realServicesToShow[0].apiService,
          );
        } else {
          setIsLoading(false);
        }
        // Includiamo nello state anche i ghost show_anyway: ServiceTypeSelect
        // li renderizzerà come card non cliccabili col testo dedicato.
        const ghostServices = servicesToShow
          .filter((s: any) => s.apiService?.isShowAnyway)
          .map((s: any) => s.apiService);
        setServices(
          ghostServices.length > 0 ? [...data, ...ghostServices] : data,
        );
      } else if (data && !!data.error) {
        console.error("ERROR FETCHING SERVICES: ", data);
        if (data.error === "max_pax_exceeded") {
          setHasErrors(true);
          if (data.maxPax) {
            setErrorMessage(
              t(
                "Al momento accettiamo al massimo prenotazioni per {{maxPax}} persone",
                {
                  maxPax: data.maxPax,
                },
              ),
            );
          } else {
            setErrorMessage(
              t(
                "Al momento accettiamo un numero limitato di persone per prenotazione",
              ),
            );
          }
        } else if (data.error === "max_man_pax_exceeded") {
          setHasErrors(true);
          if (data.maxManPax) {
            setErrorMessage(
              t(
                "Al momento accettiamo al massimo prenotazioni per {{maxManPax}} uomini",
                {
                  maxManPax: data.maxManPax,
                },
              ),
            );
          } else {
            setHasErrors(true);
          }
        } else if (data.error === "max_woman_pax_exceeded") {
          setHasErrors(true);
          if (data.maxWomanPax) {
            setErrorMessage(
              t(
                "Al momento accettiamo al massimo prenotazioni per {{maxWomanPax}} donne",
                {
                  maxWomanPax: data.maxWomanPax,
                },
              ),
            );
          } else {
            captureMessage("Error fetching services: max woman pax exceeded", {
              level: "error",
              tags: {
                event: "error_fetching_services_max_woman_pax_exceeded",
              },
              extra: {
                data: data,
              },
            });
            setHasErrors(true);
          }
        } else {
          captureMessage("Error fetching services: unknown error", {
            level: "error",
            tags: {
              event: "error_fetching_services_unknown_error",
            },
            extra: {
              data: data,
            },
          });
          setHasErrors(true);
        }
        setIsLoading(false);
      }
    } catch (error) {
      console.error("ERROR FETCHING SERVICES: ", error);
      setHasErrors(true);
      setIsLoading(false);
    }
  };

  if (hasErrors) {
    return (
      <div className="px-6 mt-6">
        <div className="w-full max-w-lg mx-auto bg-white text-secondary border-2 border-secondary-600 p-4 py-10 rounded-lg">
          <div className="flex flex-col items-center justify-center gap-2">
            <AlertCircle className="h-12 w-12 flex-shrink-0" size={48} />
            <span className="text-center block">
              {errorMessage ||
                t("Qualcosa è andato storto! Ricarica la pagina e riprova")}
            </span>
            <Button
              className="mt-4"
              variant={"muted"}
              onClick={() => {
                if (onResetToStep0) {
                  onResetToStep0();
                } else {
                  onCancel();
                }
              }}
            >
              <span>{t("Torna all'inizio")}</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      id="step-service-type"
      className="w-full max-w-3xl mx-auto flex flex-col px-8 py-8"
    >
      {!isLoading && services && services.length > 0 && !isSkipped && (
        <>
          <h3 className="font-bold text-xl text-center">{t("Servizio")}</h3>
          <p className="text-center mt-2">{t("Seleziona il servizio")}</p>
        </>
      )}

      {(isLoading || isSkipped) && (
        <div className="w-full flex items-center justify-center py-4">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-solid border-secondary border-t-transparent"></div>
        </div>
      )}

      {/* {services?.length === 0 && !isLoading && !isSkipped && services && (
        <div className="w-full bg-red-400 text-white p-4">
          <span className="text-white text-center block">
            {t("Nessun servizio trovato")}
          </span>
        </div>
      )} */}

      {selectedAvailability && !isLoading && !isSkipped && (
        <ServiceTypeSelect
          services={services || []}
          selectedValue={selectedApiService}
          isVoucherBooking={isVoucherBooking}
          groupVariantsByCmsService
          totalGuests={guestsCount.male + guestsCount.female}
          onSelect={(cmsService, apiService) => {
            if (cmsService.id !== selectedCmsService?.id) {
              onChange();
            }
            setSelectedCmsService(cmsService);
            setSelectedApiService(apiService);
            // Persisti la selezione nel genitore così sopravvive a un rimonta.
            onSelectionChange?.(cmsService, apiService);
            // Single-choice step: picking a card moves straight to the next step.
            if (!disableConfirm) {
              onConfirm(cmsService, apiService);
            }
          }}
          onSkip={(cmsService, apiService) => {
            setIsSkipped(true);
            onConfirm(cmsService, apiService);
          }}
        />
      )}
      {!isLoading && !isSkipped && (
        <div className="w-full flex flex-row items-center justify-center space-x-2 mt-4">
          <Button
            variant={"muted"}
            onClick={() => {
              onCancel();
            }}
          >
            <span>{t("Indietro")}</span>
          </Button>
          {SHOW_CONFIRM_ON_AUTO_ADVANCE_STEPS && (
            <Button
              disabled={!selectedCmsService || disableConfirm}
              variant={"secondary"}
              className="self-start"
              onClick={() => {
                onConfirm(selectedCmsService, selectedApiService);
              }}
            >
              <span>Conferma</span>
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ServiceTypeStep;
